import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useState } from "react";
import EditOutlineIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  addProjectRequest,
  editProjectRequest,
  deleteProjectRequest,
} from "../../../../utils/APICalls";
import { useDispatch } from "react-redux";
import Loading from "../../../../components/loading/loading";
import { getProjectsRequest } from "../../../../redux/orders/slice";

interface Project {
  id: string;
  name?: string;
  projectName?: string;
}

type Props = {
  formik: any;
  projects: any;
  setShowErrorNotification: Function;
  setErrorMessage: Function;
  setShowLoading: Function;
  setShowSuccessNotification: Function;
  setSuccessMessage: Function;
  disabled?: boolean;
  includeOptionalLabel?: boolean;
};

const ProjectSection = (props: Props) => {
  const {
    formik,
    projects,
    setShowErrorNotification,
    setErrorMessage,
    setShowSuccessNotification,
    setSuccessMessage,
    disabled = false,
  } = props;
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [newProjectName, setNewProjectName] = useState("");
  const [editProjectName, setEditProjectName] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const handleAddProject = () => {
    setIsAddModalOpen(true);
    setNewProjectName("");
  };

  const handleEditProject = (project: Project) => {
    setIsDropdownOpen(false);
    formik.setFieldValue("project", project.id);
    setTimeout(() => {
      setEditingProject(project);
      setEditProjectName(project.projectName || project.name || "");
      setIsEditModalOpen(true);
    }, 100);
  };

  const handleDeleteProject = () => {
    setIsDeleteModalOpen(true);
    setIsEditModalOpen(false);
  };

  const saveNewProject = async () => {
    if (newProjectName.trim()) {
      const payload = {
        clientId: formik.values.clientId,
        projectName: newProjectName,
      };
      setIsLoading(true);
      try {
        const response = await addProjectRequest(payload);
        if (response?.status === 201) {
          dispatch(getProjectsRequest({ clientId: formik.values.clientId }));
          setIsAddModalOpen(false);
          setNewProjectName("");
          setIsDropdownOpen(false);
          formik.setFieldValue("project", response.data.id);
          setSuccessMessage("Project added successfully!");
          setShowSuccessNotification(true);
        } else if (response?.status === 400) {
          setShowErrorNotification(true);
          setErrorMessage(response?.data?.detail);
        }
      } catch (error) {
        setShowErrorNotification(true);
        setErrorMessage("An error occurred while adding the project");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const saveEditedProject = async () => {
    if (editingProject && editProjectName.trim()) {
      const payload = {
        id: editingProject.id,
        projectName: editProjectName.trim(),
      };
      setIsLoading(true);
      try {
        const response = await editProjectRequest(payload);
        if (response?.status === 204) {
          setIsEditModalOpen(false);
          setEditingProject(null);
          setEditProjectName("");
          setIsDropdownOpen(false);
          setSuccessMessage("Project updated successfully!");
          setShowSuccessNotification(true);

          setTimeout(() => {
            dispatch(getProjectsRequest({ clientId: formik.values.clientId }));
          }, 100);
        } else if (response?.status === 400) {
          setShowErrorNotification(true);
          setErrorMessage(response?.data?.detail);
        }
      } catch (error) {
        setShowErrorNotification(true);
        setErrorMessage("An error occurred while editing the project");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const confirmDeleteProject = async () => {
    if (editingProject) {
      setIsLoading(true);
      try {
        const response = await deleteProjectRequest(editingProject.id);
        if (response?.status === 204) {
          setIsDeleteModalOpen(false);
          setEditingProject(null);
          setEditProjectName("");
          setIsDropdownOpen(false);
          setSuccessMessage("Project deleted successfully!");
          setShowSuccessNotification(true);

          setTimeout(() => {
            dispatch(getProjectsRequest({ clientId: formik.values.clientId }));
          }, 100);
        } else if (response?.status === 400) {
          setShowErrorNotification(true);
          setErrorMessage(response?.data?.detail);
        }
      } catch (error) {
        setShowErrorNotification(true);
        setErrorMessage("An error occurred while deleting the project");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <FormControl fullWidth size="small" variant="outlined">
        <InputLabel id="assign-project-label" shrink>
          Assign to Project?
        </InputLabel>
        <Select
          labelId="assign-project-label"
          value={formik.values.project || ""}
          onChange={(e) => {
            formik.setFieldValue("project", e.target.value || "");
          }}
          name="project"
          label="Assign to Project?"
          displayEmpty
          open={isDropdownOpen}
          onOpen={() => setIsDropdownOpen(true)}
          onClose={() => setIsDropdownOpen(false)}
          disabled={!formik.values.clientId || disabled}
          renderValue={(value) => {
            if (!value) return "Select Project";
            const project = projects?.find((p: any) => p.id === value);
            return project
              ? `${project?.projectName || project?.name || ""}`
              : "Select Project";
          }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {projects?.map((project: any) => (
            <MenuItem key={project.id} value={project.id}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography sx={{ fontSize: "14px" }}>
                  {project.projectName || project.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditProject(project);
                  }}
                  sx={{ ml: 1, color: "primary.main" }}
                >
                  <EditOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </MenuItem>
          ))}
          <MenuItem
            onClick={handleAddProject}
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              borderTop:
                projects?.length > 0 ? "1px solid #C4C4C4" : "none",
              pt: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AddIcon sx={{ color: "primary.main", fontSize: "14px" }} />
              <Typography
                sx={{
                  color: "primary.main",
                  fontWeight: "500",
                  fontSize: "14px",
                }}
              >
                ADD NEW PROJECT
              </Typography>
            </Box>
          </MenuItem>
        </Select>
      </FormControl>

      {/* Add New Project Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Add New Project</Typography>
            <IconButton
              onClick={() => {
                setIsAddModalOpen(false);
                setIsDropdownOpen(false);
              }}
            >
              <Typography>×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Title"
            placeholder="Project Name"
            fullWidth
            variant="outlined"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setIsAddModalOpen(false);
              setIsDropdownOpen(false);
            }}
            sx={{ color: "primary.main", textTransform: "none" }}
          >
            CANCEL
          </Button>
          <Button
            onClick={saveNewProject}
            variant="contained"
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              textTransform: "none",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            SAVE
          </Button>
        </DialogActions>
        {isLoading && <Loading />}
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Edit Project</Typography>
            <IconButton
              onClick={() => {
                setIsEditModalOpen(false);
                setIsDropdownOpen(false);
              }}
            >
              <Typography>×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Title"
            placeholder="Project Name"
            fullWidth
            variant="outlined"
            value={editProjectName}
            onChange={(e) => setEditProjectName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, justifyContent: "space-between" }}>
          <Button
            onClick={handleDeleteProject}
            startIcon={<DeleteIcon color="error" />}
            sx={{ color: "error.main", textTransform: "none" }}
          >
            DELETE
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditModalOpen(false);
                setIsDropdownOpen(false);
              }}
              sx={{ color: "primary.main", textTransform: "none" }}
            >
              CANCEL
            </Button>
            <Button
              onClick={saveEditedProject}
              variant="contained"
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                textTransform: "none",
                "&:hover": { backgroundColor: "primary.main" },
              }}
            >
              SAVE
            </Button>
          </Box>
        </DialogActions>
        {isLoading && <Loading />}
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Delete {editProjectName}?</Typography>
            <IconButton
              onClick={() => {
                setIsDeleteModalOpen(false);
                setIsDropdownOpen(false);
              }}
            >
              <Typography>×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Are you sure you want to delete this {editProjectName}?
            <br />
            This action can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => {
              setIsDeleteModalOpen(false);
              setIsDropdownOpen(false);
            }}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              textTransform: "none",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={confirmDeleteProject}
            variant="text"
            sx={{
              color: "error.main",
              textTransform: "none",
            }}
          >
            DELETE
          </Button>
        </DialogActions>
        {isLoading && <Loading />}
      </Dialog>
    </>
  );
};

export default ProjectSection;
